import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { TextInput, Text, Card, Section, LoadingSpinner, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

import { useDeleteAuditSession } from '../hooks/useDeleteAuditSession';

import * as styles from './DeleteAuditSessionDialog.css';

interface DeleteAuditSessionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  auditSession: FlexibleAuditSession | null;
}

/**
 * Delete Audit Session Dialog
 *
 * Specialized delete dialog for audit sessions that requires the user to type
 * the session name for confirmation to prevent accidental deletions.
 */
export function DeleteAuditSessionDialog({ isOpen, onClose, onSuccess, auditSession }: DeleteAuditSessionDialogProps) {
  const { deleteAuditSession } = useDeleteAuditSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<{ message: string; userMessage: string; correlationId?: string }[]>([]);
  const [confirmationText, setConfirmationText] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setErrors([]);
      setIsDeleting(false);
      setConfirmationText('');
    }
  }, [isOpen]);

  const handleDelete = async () => {
    if (!auditSession) return;

    setErrors([]);
    setIsDeleting(true);

    const result = await deleteAuditSession(auditSession.id, auditSession.name);

    if (result.success) {
      setIsDeleting(false);
      onClose();
      onSuccess();
    } else {
      setErrors(result.errors as { message: string; userMessage: string; correlationId?: string }[]);
      setIsDeleting(false);
    }
  };

  const isConfirmationValid = auditSession && confirmationText.trim() === auditSession.name.trim();

  if (!auditSession) {
    return null;
  }

  return (
    <Dialog
      cancelAction={{
        label: 'Cancel',
        disabled: isDeleting,
      }}
      description="This action cannot be undone."
      isOpen={isOpen}
      primaryAction={{
        label: isDeleting ? <LoadingSpinner /> : 'Yes, Delete',
        onClick: handleDelete,
        variant: 'danger',
        disabled: isDeleting || !isConfirmationValid,
      }}
      size="md"
      title="Delete Audit Session"
      onClose={onClose}
    >
      <Section spacing="medium">
        <Text>This will permanently delete all audit data, decisions, and reports.</Text>
        <Text className={styles.confirmationLabel}>Type the session name to confirm deletion:</Text>
        <Card>
          <Section spacing="small">
            <Text className={styles.sessionName}>{auditSession.name}</Text>
          </Section>
        </Card>
        <div className={styles.inputContainer}>
          <TextInput
            autoFocus
            disabled={isDeleting}
            placeholder="Type the session name here"
            value={confirmationText}
            onChange={(value: string) => setConfirmationText(value)}
          />
        </div>
      </Section>

      {errors.length > 0 && (
        <Section spacing="medium">
          {errors.map((error, index) => (
            <div key={error.correlationId || `error-${index}`} className={index < errors.length - 1 ? styles.errorContainer : ''}>
              <Text className={styles.errorMessage}>{error.userMessage}</Text>
              {error.correlationId && <Text className={styles.correlationId}>Correlation ID: {error.correlationId}</Text>}
            </div>
          ))}
        </Section>
      )}
    </Dialog>
  );
}
